import { useState } from "react";
import { AlertTriangle, Plus, CheckCircle2, XCircle, Clock, MessageSquare } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useDisputes, useCreateDispute, useResolveDispute, useUpdateDisputeStatus } from "@/hooks/useDisputes";
import { useHosts } from "@/hooks/useHosts";
import { useBookings } from "@/hooks/useBookings";

// ── Label maps ────────────────────────────────────────────────────────────────
const DISPUTE_TYPE_LABELS: Record<string, string> = {
  noise_complaint: "Noise Complaint",
  unauthorized_guests: "Unauthorized Guests",
  property_damage: "Property Damage",
  fee_dispute: "Fee Dispute",
  bylaw_violation: "Bylaw Violation",
  unauthorized_str: "Unauthorized STR",
  parking_violation: "Parking Violation",
  pet_policy_violation: "Pet Policy Violation",
  other: "Other",
};

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-orange-100 text-orange-800",
  urgent: "bg-red-100 text-red-700",
};

const SUB_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  investigating: "Investigating",
  awaiting_response: "Awaiting Response",
  escalated: "Escalated",
  closed: "Closed",
};

const RESOLUTION_ACTION_LABELS: Record<string, string> = {
  warning_issued: "Warning Issued",
  fine_levied: "Fine Levied",
  approved: "Approved / Resolved",
  dismissed: "Dismissed",
  escalated_local_gov: "Escalated to Local Government",
  no_action_required: "No Action Required",
};

export default function Disputes() {
  // File dispute dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    host_id: "",
    unit_id: "",
    booking_id: "",
    dispute_type: "",
    priority: "medium",
    complainant_type: "",
    complainant_name: "",
    complainant_unit: "",
    incident_date: "",
    amount_claimed: "",
    reason: "",
  });

  // Resolve / view dispute dialog
  const [activeDispute, setActiveDispute] = useState<any>(null);
  const [resolution, setResolution] = useState("");
  const [subStatus, setSubStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [resolutionAction, setResolutionAction] = useState("");
  const [fineAmount, setFineAmount] = useState("");
  const [commLog, setCommLog] = useState("");

  const { data: disputes = [], isLoading, error } = useDisputes();
  const { data: hosts = [] } = useHosts();
  const { data: bookings = [] } = useBookings();
  const createDispute = useCreateDispute();
  const resolveDisputeMutation = useResolveDispute();
  const updateDispute = useUpdateDisputeStatus();

  const handleHostChange = (hostId: string) => {
    const host = hosts.find((h: any) => h.id === hostId);
    setForm({ ...form, host_id: hostId, unit_id: host?.unit_id || "", booking_id: "" });
  };

  const resetForm = () => {
    setForm({
      host_id: "", unit_id: "", booking_id: "", dispute_type: "", priority: "medium",
      complainant_type: "", complainant_name: "", complainant_unit: "",
      incident_date: "", amount_claimed: "", reason: "",
    });
  };

  const handleSubmit = () => {
    if (!form.host_id || !form.unit_id || !form.reason.trim()) return;
    createDispute.mutate(
      {
        host_id: form.host_id,
        unit_id: form.unit_id,
        booking_id: form.booking_id || undefined,
        dispute_type: form.dispute_type || undefined,
        priority: form.priority,
        complainant_type: form.complainant_type || undefined,
        complainant_name: form.complainant_name || undefined,
        complainant_unit: form.complainant_unit || undefined,
        incident_date: form.incident_date || undefined,
        amount_claimed: form.amount_claimed ? parseFloat(form.amount_claimed) : undefined,
        reason: form.reason.trim(),
      },
      {
        onSuccess: () => {
          setOpen(false);
          resetForm();
        },
      }
    );
  };

  const openDialog = (dispute: any) => {
    setActiveDispute(dispute);
    setResolution(dispute.resolution ?? "");
    setSubStatus(dispute.sub_status ?? "open");
    setAssignedTo(dispute.assigned_to ?? "");
    setResolutionAction(dispute.resolution_action ?? "");
    setFineAmount(dispute.fine_amount != null ? String(dispute.fine_amount) : "");
    setCommLog(dispute.communication_log ?? "");
  };

  const closeDialog = () => {
    setActiveDispute(null);
    setResolution(""); setSubStatus(""); setAssignedTo("");
    setResolutionAction(""); setFineAmount(""); setCommLog("");
  };

  const handleSaveProgress = () => {
    if (!activeDispute) return;
    updateDispute.mutate({
      id: activeDispute.id,
      sub_status: subStatus as any || undefined,
      assigned_to: assignedTo || undefined,
      communication_log: commLog || undefined,
    }, { onSuccess: closeDialog });
  };

  const handleApprove = () => {
    if (!activeDispute) return;
    resolveDisputeMutation.mutate(
      {
        id: activeDispute.id,
        status: "approved",
        resolution: resolution.trim() || undefined,
        sub_status: "closed",
        assigned_to: assignedTo || undefined,
        resolution_action: (resolutionAction as any) || undefined,
        fine_amount: fineAmount ? parseFloat(fineAmount) : undefined,
        communication_log: commLog || undefined,
      },
      { onSuccess: closeDialog }
    );
  };

  const handleReject = () => {
    if (!activeDispute) return;
    resolveDisputeMutation.mutate(
      {
        id: activeDispute.id,
        status: "rejected",
        resolution: resolution.trim() || undefined,
        sub_status: "closed",
        assigned_to: assignedTo || undefined,
        resolution_action: (resolutionAction as any) || undefined,
        fine_amount: fineAmount ? parseFloat(fineAmount) : undefined,
        communication_log: commLog || undefined,
      },
      { onSuccess: closeDialog }
    );
  };

  // Bookings filtered to selected host
  const hostBookings = bookings.filter((b: any) => b.host_id === form.host_id);

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-destructive">Failed to load disputes.</p>
      </div>
    );
  }

  const pendingCount = disputes.filter((d: any) => d.status === "pending").length;
  const urgentCount = disputes.filter((d: any) => d.priority === "urgent").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Disputes</h1>
          <p className="text-sm text-muted-foreground">
            {pendingCount} pending{urgentCount > 0 ? ` · ${urgentCount} urgent` : ""} — file, investigate and document all disputes
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> File Dispute</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : disputes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No disputes</p>
              <p className="text-xs text-muted-foreground">No disputes have been filed yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Unit</TableHead>
                  <TableHead className="text-xs">Host</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Priority</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Sub-Status</TableHead>
                  <TableHead className="text-xs">Incident Date</TableHead>
                  <TableHead className="text-xs">Filed</TableHead>
                  <TableHead className="text-xs w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disputes.map((d: any) => (
                  <TableRow key={d.id}>
                    <TableCell className="text-sm font-medium">{d.unit?.unit_number || "—"}</TableCell>
                    <TableCell className="text-xs">{d.host?.profile?.full_name || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {d.dispute_type ? (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 whitespace-nowrap">
                          {DISPUTE_TYPE_LABELS[d.dispute_type] ?? d.dispute_type}
                        </Badge>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded capitalize ${PRIORITY_COLORS[d.priority ?? "medium"]}`}>
                        {d.priority ?? "medium"}
                      </span>
                    </TableCell>
                    <TableCell><StatusBadge status={d.status || "pending"} /></TableCell>
                    <TableCell className="text-xs capitalize">
                      {(d.sub_status ?? "open").replace(/_/g, " ")}
                    </TableCell>
                    <TableCell className="text-xs">
                      {d.incident_date ? new Date(d.incident_date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{new Date(d.filed_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        variant={d.status === "pending" ? "outline" : "ghost"}
                        size="sm"
                        className="h-7 text-xs px-2"
                        onClick={() => openDialog(d)}
                      >
                        {d.status === "pending" ? "Manage" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── View / Manage Dispute Dialog ──────────────────────────────── */}
      <Dialog open={!!activeDispute} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeDispute?.status === "pending" ? "Manage Dispute" : "Dispute Record"}
            </DialogTitle>
          </DialogHeader>
          {activeDispute && (
            <div className="space-y-4 py-1">
              {/* ── Info grid ── */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Unit</p>
                  <p className="font-medium">{activeDispute.unit?.unit_number || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Host</p>
                  <p className="font-medium">{activeDispute.host?.profile?.full_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Dispute Type</p>
                  <p className="font-medium">{activeDispute.dispute_type ? DISPUTE_TYPE_LABELS[activeDispute.dispute_type] : "General"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Priority</p>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded capitalize ${PRIORITY_COLORS[activeDispute.priority ?? "medium"]}`}>
                    {activeDispute.priority ?? "medium"}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={activeDispute.status || "pending"} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Incident Date</p>
                  <p className="font-medium">{activeDispute.incident_date ? new Date(activeDispute.incident_date).toLocaleDateString() : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Filed</p>
                  <p className="font-medium">{new Date(activeDispute.filed_at).toLocaleString()}</p>
                </div>
                {activeDispute.amount_claimed != null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Amount Claimed</p>
                    <p className="font-medium">${activeDispute.amount_claimed.toFixed(2)}</p>
                  </div>
                )}
                {activeDispute.complainant_type && (
                  <div>
                    <p className="text-xs text-muted-foreground">Complainant</p>
                    <p className="font-medium capitalize">{activeDispute.complainant_type.replace(/_/g, " ")}
                      {activeDispute.complainant_name ? ` — ${activeDispute.complainant_name}` : ""}
                      {activeDispute.complainant_unit ? ` (Unit ${activeDispute.complainant_unit})` : ""}
                    </p>
                  </div>
                )}
                {activeDispute.booking && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Related Booking</p>
                    <p className="font-medium text-xs">
                      Guest: {activeDispute.booking.guest_name} — {activeDispute.booking.check_in} to {activeDispute.booking.check_out}
                    </p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Description / Reason</p>
                  <p className="text-sm bg-muted rounded-md p-2">{activeDispute.reason}</p>
                </div>
              </div>

              {/* Existing resolution notes (if resolved) */}
              {activeDispute.resolution && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs font-medium">Resolution Notes</p>
                  <p className="text-sm mt-1">{activeDispute.resolution}</p>
                </div>
              )}
              {activeDispute.resolved_at && (
                <p className="text-xs text-muted-foreground">
                  Resolved on {new Date(activeDispute.resolved_at).toLocaleString()}
                  {activeDispute.resolution_action ? ` · ${RESOLUTION_ACTION_LABELS[activeDispute.resolution_action]}` : ""}
                  {activeDispute.fine_amount != null ? ` · Fine: $${activeDispute.fine_amount}/day` : ""}
                </p>
              )}

              {/* ── Editable fields ── */}
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Sub-Status</Label>
                  <Select value={subStatus || "open"} onValueChange={setSubStatus}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="awaiting_response">Awaiting Response</SelectItem>
                      <SelectItem value="escalated">Escalated</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Assigned To</Label>
                  <Input
                    placeholder="Name of handler"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
              </div>

              {/* Communication log */}
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1.5">
                  <MessageSquare className="h-3 w-3" /> Communication Log / Investigation Notes
                </Label>
                <Textarea
                  placeholder="Add notes, communications, or investigation findings (appended to record)..."
                  rows={3}
                  value={commLog}
                  onChange={(e) => setCommLog(e.target.value)}
                  className="text-xs"
                />
              </div>

              {/* Save-progress button (non-resolving) */}
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={handleSaveProgress}
                disabled={updateDispute.isPending}
              >
                Save Progress (without resolving)
              </Button>

              {/* ── Resolution (only for pending) ── */}
              {activeDispute.status === "pending" && (
                <>
                  <Separator />
                  <p className="text-xs font-medium text-muted-foreground">Resolution</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Resolution Action</Label>
                      <Select value={resolutionAction || "none"} onValueChange={(v) => setResolutionAction(v === "none" ? "" : v)}>
                        <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select action" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          <SelectItem value="warning_issued">Warning Issued</SelectItem>
                          <SelectItem value="fine_levied">Fine Levied</SelectItem>
                          <SelectItem value="approved">Approved / Resolved</SelectItem>
                          <SelectItem value="dismissed">Dismissed</SelectItem>
                          <SelectItem value="escalated_local_gov">Escalated to Local Government</SelectItem>
                          <SelectItem value="no_action_required">No Action Required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">
                        Fine Amount ($/day) <span className="text-muted-foreground">per your strata bylaws</span>
                      </Label>
                      <Input
                        type="number" min="0" max="1000" step="50"
                        placeholder="0"
                        value={fineAmount}
                        onChange={(e) => setFineAmount(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="resolution_notes" className="text-xs">Resolution Notes (optional)</Label>
                    <Textarea
                      id="resolution_notes"
                      placeholder="Describe the resolution decision and any actions taken..."
                      rows={3}
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      className="text-xs"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-success hover:bg-success/90 text-success-foreground"
                      onClick={handleApprove}
                      disabled={resolveDisputeMutation.isPending}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Approve / Resolve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={handleReject}
                      disabled={resolveDisputeMutation.isPending}
                    >
                      <XCircle className="mr-1 h-3 w-3" /> Reject / Dismiss
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── File Dispute Dialog ──────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); resetForm(); } else setOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>File a Dispute</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">

            {/* Host & Unit */}
            <div className="space-y-2">
              <Label>Host / Unit *</Label>
              <Select value={form.host_id} onValueChange={handleHostChange}>
                <SelectTrigger><SelectValue placeholder="Select host & unit" /></SelectTrigger>
                <SelectContent>
                  {hosts.map((h: any) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.profile?.full_name || "Unknown"} — Unit {h.unit?.unit_number || "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional: Related Booking */}
            {form.host_id && hostBookings.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs">Related Booking (optional)</Label>
                <Select
                  value={form.booking_id || "none"}
                  onValueChange={(v) => setForm({ ...form, booking_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select booking if applicable" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not booking-related</SelectItem>
                    {hostBookings.map((b: any) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.guest_name} — {b.check_in} to {b.check_out}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Dispute Type</Label>
                <Select value={form.dispute_type || "none"} onValueChange={(v) => setForm({ ...form, dispute_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="noise_complaint">Noise Complaint</SelectItem>
                    <SelectItem value="unauthorized_guests">Unauthorized Guests</SelectItem>
                    <SelectItem value="property_damage">Property Damage</SelectItem>
                    <SelectItem value="fee_dispute">Fee Dispute</SelectItem>
                    <SelectItem value="bylaw_violation">Bylaw Violation</SelectItem>
                    <SelectItem value="unauthorized_str">Unauthorized STR</SelectItem>
                    <SelectItem value="parking_violation">Parking Violation</SelectItem>
                    <SelectItem value="pet_policy_violation">Pet Policy Violation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Incident Date & Amount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident_date" className="text-xs">Date of Incident</Label>
                <Input
                  id="incident_date"
                  type="date"
                  value={form.incident_date}
                  onChange={(e) => setForm({ ...form, incident_date: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount_claimed" className="text-xs">Amount Claimed ($) <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  id="amount_claimed"
                  type="number" min="0" step="0.01"
                  placeholder="0.00"
                  value={form.amount_claimed}
                  onChange={(e) => setForm({ ...form, amount_claimed: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>

            {/* Complainant Info */}
            <Separator />
            <p className="text-xs text-muted-foreground font-medium">Complainant Info (optional)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Complainant Type</Label>
                <Select value={form.complainant_type || "none"} onValueChange={(v) => setForm({ ...form, complainant_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Who is filing?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="owner">Unit Owner</SelectItem>
                    <SelectItem value="strata_council">Strata Council</SelectItem>
                    <SelectItem value="neighbor">Neighbour</SelectItem>
                    <SelectItem value="guest">Guest</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="complainant_unit" className="text-xs">Complainant Unit # (if neighbour)</Label>
                <Input
                  id="complainant_unit"
                  placeholder="e.g. 204"
                  value={form.complainant_unit}
                  onChange={(e) => setForm({ ...form, complainant_unit: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="complainant_name" className="text-xs">Complainant Name <span className="text-muted-foreground">(optional)</span></Label>
              <Input
                id="complainant_name"
                placeholder="Full name of complainant"
                value={form.complainant_name}
                onChange={(e) => setForm({ ...form, complainant_name: e.target.value })}
                className="h-9"
              />
            </div>

            {/* Reason / Description */}
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="reason">Description / Reason *</Label>
              <Textarea
                id="reason"
                placeholder="Describe the dispute in detail — what happened, when, who was involved, and any evidence..."
                rows={4}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createDispute.isPending || !form.host_id || !form.reason.trim()}>
              {createDispute.isPending ? "Filing..." : "File Dispute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
